import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, X, FileText, Database, Calendar, HardDrive, AlertCircle, CheckCircle, Loader, Settings, Trash2, Pencil, Check, ArrowUpDown, Download, Shield } from 'lucide-react';
import { useOcelContext } from '../../../stores/OcelContext';
import { useAuth } from '../../../stores/AuthContext';
import { getActiveIntlLocale } from '../../../i18n/intlLocale';
import { formatNumber } from '../../../utils/numberFormat';

// Import your existing API client functions
import {
  getAvailableFiles,
  getActiveFileType,
  uploadOCELFile,
  deleteOCELFile,
  updateFileDisplayName,
  getOCELMetadata,
  getResourceValues,
  downloadOCELFile,
  validateOCELFile,
  formatFileSize,
  getETLCustomers
} from '../../../services/apiClient'
import { getStoredActiveFile, setStoredActiveFile } from '../../../services/activeFileStorage';
import { identifierFromFilename, sanitizeIdentifier } from '../../../utils/sanitizeIdentifier';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import FileAccessEditor, { accessSummary } from './FileAccessEditor';

const OCELFileManager = ({ activeFile: activeFileProp, onFileChange, onDisplayNameChange, className = '', disableFixedPositioning = false }) => {
  const { setMetadata: setGlobalMetadata, setMetadataLoading } = useOcelContext(); // Get context setter
  const { isAdmin, canAccessProjects } = useAuth();
  const { t } = useTranslation('file-manager');
  const [isOpen, setIsOpen] = useState(false);
  // Admin-only per-file access control state
  const [customers, setCustomers] = useState([]);
  const [uploadCustomer, setUploadCustomer] = useState('');
  const [accessEditFor, setAccessEditFor] = useState(null); // file_type | null
  const [files, setFiles] = useState([]);
  // Per-browser selection: driven by the activeFile prop from App (which owns
  // the localStorage-persisted value) — never read from the server-global state.
  const [activeFile, setActiveFile] = useState(activeFileProp ?? getStoredActiveFile());
  const [metadata, setMetadata] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showMetadata, setShowMetadata] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [isDownloading, setIsDownloading] = useState(null);
  const [uploadDisplayName, setUploadDisplayName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null); // File | null — staged, not yet uploaded
  const [sourceName, setSourceName] = useState(''); // sanitized identifier, editable before confirming upload
  const [editingName, setEditingName] = useState(null); // { fileType, value } | null
  const [isSavingName, setIsSavingName] = useState(false);
  // eslint-disable-next-line i18next/no-literal-string -- internal sort key, compared below and used as <option value>; never rendered
  const [sortKey, setSortKey] = useState('upload_date'); // 'name' | 'upload_date' | 'file_size'
  // eslint-disable-next-line i18next/no-literal-string -- internal sort direction enum; the visible labels are list.sortAscending/Descending
  const [sortDir, setSortDir] = useState('desc'); // 'asc' | 'desc'
  const fileInputRef = useRef(null);

  // Fetch available files using your existing API
  const fetchFiles = useCallback(async () => {
    try {
      console.log('🔄 Fetching available files...');
      const data = await getAvailableFiles();
      console.log('✅ Files fetched:', data);
      setFiles(data);
    } catch (error) {
      console.error('❌ Error fetching files:', error);
      setError(t('errors.loadFiles', { detail: error.message }));
    }
  }, []);

  // Keep local state in sync with the selection owned by App
  useEffect(() => {
    if (activeFileProp) {
      setActiveFile(activeFileProp);
    }
  }, [activeFileProp]);

  // Monotonic token so a slow metadata response from a previous dataset
  // switch can never overwrite the metadata of the current one.
  const metadataSeqRef = useRef(0);

  // Fetch metadata + resource values for active file and store in global context
  const fetchMetadata = useCallback(async () => {
    if (!activeFile) return;

    const seq = ++metadataSeqRef.current;
    setMetadataLoading(true);
    try {
      const [data, resourceData] = await Promise.all([
        getOCELMetadata(activeFile),
        getResourceValues(activeFile),
      ]);
      if (seq !== metadataSeqRef.current) return; // a newer fetch superseded this one
      const enriched = {
        ...data,
        resource_values: resourceData.values || [],
        // 'ressource' is an OCEL data column name (backend contract), not UI text.
        resource_column: resourceData.used_column
          ? resourceData.used_column.replace(/^ocel:/, '')
          : 'ressource',
      };
      setMetadata(enriched);
      setGlobalMetadata(enriched);
    } catch (error) {
      if (seq !== metadataSeqRef.current) return;
      console.error('❌ Error fetching metadata:', error);
      setError(t('errors.loadMetadata', { detail: error.message }));
    } finally {
      if (seq === metadataSeqRef.current) {
        setMetadataLoading(false);
      }
    }
  }, [activeFile, setGlobalMetadata, setMetadataLoading]);

  // Load initial data
  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // Admins: load the ETL customer list once the manager is opened, to populate
  // the per-file access editor. Best-effort — failure just leaves it empty.
  useEffect(() => {
    if (!isOpen) return;
    getETLCustomers()
      .then((rows) => {
        setCustomers(rows);
        setUploadCustomer((current) => rows.some((c) => c.customer_id === current) ? current : rows.length === 1 ? rows[0].customer_id : '');
      })
      .catch((e) => console.warn('Could not load ETL customers for access editor:', e));
  }, [isOpen]);

  // Notify parent of the active file's current display name whenever
  // files list or active selection changes (covers initial load + rename)
  useEffect(() => {
    if (!activeFile || !files.length || !onDisplayNameChange) return;
    const f = files.find(f => f.file_type === activeFile);
    if (f) onDisplayNameChange(f.display_name);
  }, [files, activeFile, onDisplayNameChange]);

  // Load metadata when active file changes
  useEffect(() => {
    if (activeFile) {
      fetchMetadata();
    }
  }, [activeFile, fetchMetadata]);

  // Stage a file for upload — does not upload yet, gives the user a chance to
  // set a Source Name (the resulting file_type identifier) first.
  const handleFileSelected = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setError(null);
    setSuccess(null);
    setSelectedFile(file);
    setSourceName(identifierFromFilename(file.name));
  };

  const handleCancelSelection = () => {
    setSelectedFile(null);
    setSourceName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const existingFileTypes = new Set(files.map(f => f.file_type));
  const trimmedSourceName = sourceName.trim();
  // The registered file_type is never customer-prefixed — the backend tracks
  // ownership separately via OCELFileInfo.customer_id (uploadCustomer, sent
  // as its own request parameter below) — so the collision check and the
  // "stored as" preview must both use the plain, unprefixed name to match
  // what the backend will actually register.
  const storedFileType = trimmedSourceName;
  const sourceNameError = !uploadCustomer
    ? t('upload.customerRequired')
    : !trimmedSourceName
    ? t('upload.sourceNameRequired')
    : existingFileTypes.has(storedFileType)
      ? t('upload.sourceNameTaken')
      : null;

  // Handle file upload using your existing API
  const handleFileUpload = async () => {
    const file = selectedFile;
    if (!file || sourceNameError) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);
    setSuccess(null);

    try {
      console.log('🔄 Validating file:', file.name);

      // Validate file using your existing validation function
      await validateOCELFile(file);
      console.log('✅ File validation passed');

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      console.log('🔄 Uploading file...');
      const result = await uploadOCELFile(file, trimmedSourceName, uploadDisplayName || null, uploadCustomer);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        console.log('✅ Upload successful:', result);
        setSuccess(t('status.uploadSuccess', { filename: file.name }));
        setUploadDisplayName('');
        handleCancelSelection();
        await fetchFiles(); // Refresh file list
        setTimeout(() => setSuccess(null), 3000);
      } else {
        // NOTE: `new Error('…')` is invisible to i18next/no-literal-string at every
        // setting (^Error$ is in callees.exclude) — this string is tracked in the
        // manual checklist in docs/i18n-migration.md, not by the linter.
        throw new Error(result.error || t('errors.uploadUnknown'));
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      setError(t('errors.upload', { detail: error.message }));
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // Handle file deletion using your existing API
  const handleFileDelete = async (fileType) => {
    // Native confirm() is retained deliberately (no confirm dialog exists in
    // components/ui). Accepted cost: its OK/Cancel buttons follow the OS locale
    // and cannot be translated — only the message can.
    if (!confirm(t('list.deleteConfirm'))) return;

    setIsDeleting(fileType);
    try {
      console.log('🔄 Deleting file:', fileType);
      await deleteOCELFile(fileType);
      console.log('✅ File deleted successfully');
      
      setSuccess(t('status.deleteSuccess'));
      await fetchFiles(); // Refresh file list
      
      // If the deleted file was this browser's selection, fall back to the
      // server default; if none exists, send the user back to the Projects page
      if (activeFile === fileType) {
        setStoredActiveFile(null);
        let fallback = null;
        try {
          const data = await getActiveFileType();
          if (data.active_file_type && data.active_file_type !== fileType) {
            fallback = data.active_file_type;
          }
        } catch (_) {
          // No server default reachable — go to project selection
        }
        if (fallback) {
          setStoredActiveFile(fallback);
          setActiveFile(fallback);
          if (onFileChange) {
            onFileChange(fallback);
          }
        } else if (canAccessProjects) {
          // eslint-disable-next-line i18next/no-literal-string -- router path, not UI text
          window.location.assign('/projects');
          return;
        } else {
          // No fallback dataset and this user can't get into /projects to pick
          // one — navigating there would just bounce them straight back
          // (ProtectedRoute redirects non-opted-in users away from /projects),
          // leaving them on a page with no active dataset and no explanation.
          // Clear the just-set delete-success message so it doesn't linger
          // forever alongside this error (there's no timeout to clear it on
          // this early-return path, unlike the normal flow below).
          setSuccess(null);
          setError(t('errors.noProjectAccess'));
          setTimeout(() => setError(null), 5000);
          return;
        }
      }
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('❌ Delete error:', error);
      setError(t('errors.delete', { detail: error.message }));
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsDeleting(null);
    }
  };

  // Handle file download using your existing API
  const handleFileDownload = async (fileType, displayName) => {
    setIsDownloading(fileType);
    try {
      console.log('🔄 Downloading file:', fileType);
      await downloadOCELFile(fileType, displayName);
      console.log('✅ File download started');
    } catch (error) {
      console.error('❌ Download error:', error);
      setError(t('errors.download', { detail: error.message }));
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsDownloading(null);
    }
  };

  // Switch this browser's dataset. Deliberately does NOT touch the
  // server-global active file — that is only a first-visit default, and
  // writing it here would affect other users' sessions.
  const handleSetActive = (fileType) => {
    setMetadataLoading(true);
    setIsOpen(false);
    console.log('🔄 Switching dataset:', fileType);

    setStoredActiveFile(fileType);
    setActiveFile(fileType);
    // H2: fileType is a data identifier — interpolated, never translated.
    setSuccess(t('status.activeFileSet', { fileType }));
    setTimeout(() => setSuccess(null), 3000);

    // Notify parent component of file change
    if (onFileChange) {
      onFileChange(fileType);
    }
  };

  const handleSaveDisplayName = async () => {
    if (!editingName) return;
    const trimmed = editingName.value.trim();
    if (!trimmed || trimmed.length > 100) return;
    setIsSavingName(true);
    try {
      await updateFileDisplayName(editingName.fileType, trimmed);
      await fetchFiles();
      setEditingName(null);
    } catch (err) {
      setError(t('errors.rename', { detail: err.message }));
      setTimeout(() => setError(null), 5000);
    } finally {
      setIsSavingName(false);
    }
  };

  const sortedFiles = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    // Defense-in-depth: a per-worker registry can briefly still list a file
    // another Gunicorn worker already deleted from disk (exists === false)
    // until that worker's own registry catches up — never render it, since
    // its Delete button would be a no-op and Download would 404.
    return files.filter((file) => file.exists !== false).sort((a, b) => {
      if (sortKey === 'name') {
        return dir * (a.display_name || '').localeCompare(b.display_name || '');
      }
      if (sortKey === 'file_size') {
        return dir * ((a.file_size || 0) - (b.file_size || 0));
      }
      return dir * (new Date(a.upload_date).getTime() - new Date(b.upload_date).getTime());
    });
  }, [files, sortKey, sortDir]);

  // Format date for display. Follows the active UI language — this used to be
  // hardcoded 'en-US', which rendered English month abbreviations ("Feb 19")
  // inside an otherwise German UI.
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(getActiveIntlLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Date-only variant for the metadata date range.
  const formatDateOnly = (dateString) =>
    new Date(dateString).toLocaleDateString(getActiveIntlLocale(), { dateStyle: 'medium' });

  // Counts previously used `value?.toLocaleString()`, which renders BLANK when the
  // field is missing. formatNumber(undefined) would return '0' — an assertion the
  // data doesn't make — so the null case is kept blank deliberately.
  const formatCount = (value) =>
    value == null ? '' : formatNumber(value, { decimals: 0 });

  // Render collapsed state
  return (
    <>
      <div className={disableFixedPositioning ? className : `fixed top-4 left-4 z-50 ${className}`}>
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-md hover:shadow-lg transition-shadow"
        >
          <Database className="w-5 h-5" />
          <span>{t('trigger.label')}</span>
          {activeFile && (
            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
              {files.find(f => f.file_type === activeFile)?.display_name ?? activeFile}
            </span>
          )}
        </button>
      </div>

      {/* Render main file manager interface */}
      {isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">{t('title')}</h2>
          <button
            onClick={() => setIsOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title={t('actions.closeDialog')}
            aria-label={t('actions.closeDialog')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain p-6">
          {/* Status Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-700">{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-green-700">{success}</span>
            </div>
          )}

          {/* Upload Section */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">{t('upload.heading')}</h3>
            <FieldGroup className="mb-3">
              <Field data-disabled={isUploading}>
                <FieldLabel htmlFor="upload-customer">{t('upload.customerLabel')}</FieldLabel>
                <Select value={uploadCustomer} onValueChange={setUploadCustomer} disabled={isUploading || !customers.length}>
                  <SelectTrigger id="upload-customer">
                    <SelectValue placeholder={t('upload.customerRequired')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {customers.map((customer) => (
                        <SelectItem key={customer.customer_id} value={customer.customer_id}>
                          {customer.display_name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>
            </FieldGroup>
            <div className="mb-3">
              <label htmlFor="upload-display-name" className="block text-sm font-medium text-gray-700 mb-1">
                {t('upload.displayNameLabel')} <span className="text-gray-400 font-normal">{t('upload.displayNameOptional')}</span>
              </label>
              <input
                id="upload-display-name"
                type="text"
                value={uploadDisplayName}
                onChange={(e) => setUploadDisplayName(e.target.value)}
                maxLength={100}
                placeholder={t('upload.displayNamePlaceholder')}
                disabled={isUploading}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
              />
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 mb-2">
                {t('upload.dropzoneHint')}
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileSelected}
                disabled={isUploading}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                  isUploading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 cursor-pointer'
                } transition-colors`}
              >
                <Upload className="w-4 h-4 mr-2" />
                {selectedFile ? t('actions.chooseDifferentFile') : t('actions.selectFile')}
              </label>
            </div>

            {selectedFile && (
              <div className="mt-3 p-4 border border-blue-200 bg-blue-50 rounded-lg space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                  <span className="font-medium">{selectedFile.name}</span>
                  <span className="text-gray-400">({formatFileSize(selectedFile.size)})</span>
                </div>

                <div>
                  <label htmlFor="upload-source-name" className="block text-sm font-medium text-gray-700 mb-1">
                    {t('upload.sourceNameLabel')} <span className="text-gray-400 font-normal">{t('upload.sourceNameHint')}</span>
                  </label>
                  <input
                    id="upload-source-name"
                    type="text"
                    value={sourceName}
                    onChange={(e) => setSourceName(sanitizeIdentifier(e.target.value))}
                    disabled={isUploading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                  />
                  {sourceNameError ? (
                    <p className="mt-1 text-xs text-red-600">{sourceNameError}</p>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500">{t('upload.storedAs')} <span className="font-mono">{storedFileType}</span></p>
                  )}
                </div>

                {isUploading ? (
                  <div>
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Loader className="w-4 h-4 animate-spin" />
                      {t('upload.progress', { percent: uploadProgress })}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleFileUpload}
                      disabled={!!sourceNameError}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md transition-colors"
                    >
                      {t('actions.upload')}
                    </button>
                    <button
                      onClick={handleCancelSelection}
                      className="px-4 py-2 border border-gray-300 hover:bg-gray-100 text-sm font-medium rounded-md text-gray-700 transition-colors"
                    >
                      {t('common:actions.cancel')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Files List */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium text-gray-900">{t('list.heading')}</h3>
              <div className="flex items-center gap-1.5">
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {/* value= is the sort key compared in sortedFiles — translate the
                      label only, never the value. */}
                  <option value="upload_date">{t('list.sortByDate')}</option>
                  <option value="name">{t('list.sortByName')}</option>
                  <option value="file_size">{t('list.sortBySize')}</option>
                </select>
                <button
                  onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                  className="p-1.5 border border-gray-300 rounded-md text-gray-500 hover:bg-gray-50 transition-colors"
                  title={sortDir === 'asc' ? t('list.sortAscending') : t('list.sortDescending')}
                >
                  <ArrowUpDown className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {sortedFiles.map((file) => (
                <div
                  key={file.file_type}
                  className={`p-4 border rounded-lg ${
                    activeFile === file.file_type
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } transition-colors`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-400 shrink-0" />
                      <div>
                        <div className="flex items-center gap-1">
                          {editingName?.fileType === file.file_type ? (
                            <>
                              <input
                                type="text"
                                value={editingName.value}
                                onChange={(e) => setEditingName({ ...editingName, value: e.target.value })}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleSaveDisplayName();
                                  if (e.key === 'Escape') setEditingName(null);
                                }}
                                maxLength={100}
                                autoFocus
                                className="border border-blue-400 rounded px-2 py-0.5 text-sm font-medium text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              />
                              <button
                                onClick={handleSaveDisplayName}
                                disabled={isSavingName}
                                className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                                title={t('common:actions.save')}
                              >
                                {isSavingName ? <Loader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => setEditingName(null)}
                                className="p-1 text-gray-500 hover:text-gray-700"
                                title={t('common:actions.cancel')}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <h4 className="font-medium text-gray-900">{file.display_name}</h4>
                              <button
                                onClick={() => setEditingName({ fileType: file.file_type, value: file.display_name })}
                                className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
                                title={t('actions.editDisplayName')}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>{formatFileSize(file.file_size)}</span>
                          <span>{formatDate(file.upload_date)}</span>
                          {file.is_user_upload && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                              {t('list.userUploadBadge')}
                            </span>
                          )}
                          {isAdmin && (
                            <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs flex items-center gap-1" title={t('access.badgeTooltip')}>
                              <Shield className="w-3 h-3" />
                              {accessSummary(file, customers, t)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {activeFile === file.file_type ? (
                        <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                          {t('list.activeBadge')}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSetActive(file.file_type)}
                          className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 border border-blue-300 hover:border-blue-400 rounded-lg transition-colors"
                        >
                          {t('actions.setActive')}
                        </button>
                      )}
                      <button
                        onClick={() => handleFileDownload(file.file_type, file.display_name)}
                        disabled={isDownloading === file.file_type}
                        className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                        title={t('actions.downloadFile')}
                      >
                        {isDownloading === file.file_type ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => setAccessEditFor(accessEditFor === file.file_type ? null : file.file_type)}
                          className={`p-1 rounded transition-colors ${
                            accessEditFor === file.file_type
                              ? 'text-indigo-700 bg-indigo-100'
                              : 'text-gray-500 hover:text-indigo-700 hover:bg-indigo-50'
                          }`}
                          title={t('access.manageTooltip')}
                        >
                          <Shield className="w-4 h-4" />
                        </button>
                      )}
                      {file.is_user_upload && (
                        <button
                          onClick={() => handleFileDelete(file.file_type)}
                          disabled={isDeleting === file.file_type}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          title={t('actions.deleteFile')}
                          aria-label={t('actions.deleteFile')}
                        >
                          {isDeleting === file.file_type ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  {isAdmin && accessEditFor === file.file_type && (
                    <FileAccessEditor
                      file={file}
                      customers={customers}
                      onSaved={fetchFiles}
                      onClose={() => setAccessEditFor(null)}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Metadata Section */}
          {metadata && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-medium text-gray-900">{t('metadata.heading')}</h3>
                <button
                  onClick={() => setShowMetadata(!showMetadata)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title={t('metadata.toggleTooltip')}
                  aria-label={t('metadata.toggleTooltip')}
                  aria-expanded={showMetadata}
                >
                  <Settings className="w-5 h-5" />
                </button>
              </div>
              
              {showMetadata && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">{t('metadata.overviewHeading')}</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      <div className="flex justify-between">
                        <span>{t('metadata.objectsLabel')}</span>
                        <span>{formatCount(metadata.object_count)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('metadata.eventsLabel')}</span>
                        <span>{formatCount(metadata.event_count)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('metadata.dateRangeLabel')}</span>
                        <span className="text-xs">
                          {t('metadata.dateRangeValue', {
                            start: formatDateOnly(metadata.date_range?.start),
                            end: formatDateOnly(metadata.date_range?.end),
                          })}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">{t('metadata.objectTypesHeading')}</h4>
                    <div className="space-y-1 text-sm text-gray-600">
                      {Object.entries(metadata.object_types || {}).map(([type, count]) => (
                        <div key={type} className="flex justify-between">
                          {/* `type` is an OCEL object type name from the dataset — H2: never translated */}
                          <span>{type}:</span>
                          <span>{formatCount(count)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">
              {/* H2: activeFile is a data identifier — rendered as-is, never translated */}
              {t('footer.activeDataset')} <strong>{activeFile}</strong>
            </span>
            <button
              onClick={() => setIsOpen(false)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('common:actions.close')}
            </button>
          </div>
        </div>
      </div>
    </div>}
    </>
  );
};

export default OCELFileManager;